import { type VideoHTMLAttributes, useEffect, useRef } from 'react';

interface HlsVideoProps extends VideoHTMLAttributes<HTMLVideoElement> {
  /** Video source - can be .m3u8 (HLS) or .mp4 (fallback) */
  src: string;
  /** Fallback MP4 source for browsers that don't support HLS */
  fallbackSrc?: string;
  /** Poster image to show before video loads */
  poster?: string;
}

/**
 * HLS Video component that supports adaptive bitrate streaming
 *
 * Features:
 * - Automatic HLS.js lazy loading for non-Safari browsers
 * - Native HLS support detection for Safari
 * - Fallback to MP4 if HLS is not supported
 *
 * @example
 * // HLS source
 * <HlsVideo
 *   src="/assets/video/hls/hero/master.m3u8"
 *   autoPlay
 *   muted
 *   loop
 *   playsInline
 * />
 *
 * @example
 * // With MP4 fallback
 * <HlsVideo
 *   src="/assets/video/hls/hero/master.m3u8"
 *   fallbackSrc="/assets/video/hero.mp4"
 *   autoPlay
 *   muted
 *   loop
 * />
 */
export const HlsVideo = ({ src, fallbackSrc, poster, loop, ...props }: HlsVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<unknown>(null);
  const isHls = src.endsWith('.m3u8');

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // For non-HLS sources, just set the src directly
    if (!isHls) {
      video.src = src;
      return;
    }

    // Safari supports HLS natively
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      return;
    }

    // Dynamically import HLS.js only when needed
    let cleanup: (() => void) | undefined;

    const initHls = async () => {
      try {
        const Hls = (await import('hls.js')).default;

        // Use HLS.js for other browsers
        if (Hls.isSupported()) {
          const hls = new Hls({
            // Instant playback optimizations
            autoStartLoad: true,
            startLevel: 0, // Start with lowest quality for instant playback
            capLevelToPlayerSize: false, // Allow quality upgrade after start

            // Aggressive buffer settings for fast start
            maxBufferLength: 10, // Reduced from 30
            maxMaxBufferLength: 20, // Reduced from 60
            maxBufferSize: 30 * 1000 * 1000, // 30MB
            maxBufferHole: 0.3, // Tolerate small gaps

            // Fast fragment loading
            lowLatencyMode: false,
            backBufferLength: 0, // Don't keep old segments

            // Instant manifest and fragment loading
            manifestLoadingTimeOut: 5000,
            manifestLoadingMaxRetry: 1,
            levelLoadingTimeOut: 5000,
            levelLoadingMaxRetry: 2,
            fragLoadingTimeOut: 10000,
            fragLoadingMaxRetry: 2,

            // Start playback ASAP
            liveSyncDurationCount: 1,
            liveMaxLatencyDurationCount: 3,

            // Custom fetch setup for high priority loading
            fetchSetup: (context, initParams) => {
              const request = new Request(context.url, {
                ...initParams,
                priority: 'high',
              });
              return request;
            },
          });

          hlsRef.current = hls;
          hls.loadSource(src);
          hls.attachMedia(video);

          // Handle looping for HLS streams
          if (loop) {
            const handleEnded = () => {
              // Seek to beginning and play again
              video.currentTime = 0;
              video.play().catch((e) => console.error('Failed to restart video loop:', e));
            };
            
            video.addEventListener('ended', handleEnded);
            
            // Clean up event listener
            const originalCleanup = cleanup;
            cleanup = () => {
              video.removeEventListener('ended', handleEnded);
              originalCleanup?.();
            };
          }

          hls.on(Hls.Events.ERROR, (_event: unknown, data: { fatal?: boolean; type?: string }) => {
            if (data.fatal) {
              switch (data.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                  // Try to recover from network errors
                  hls.startLoad();
                  break;
                case Hls.ErrorTypes.MEDIA_ERROR:
                  // Try to recover from media errors
                  hls.recoverMediaError();
                  break;
                default:
                  // Fall back to MP4 if available
                  if (fallbackSrc) {
                    video.src = fallbackSrc;
                  }
                  hls.destroy();
                  break;
              }
            }
          });

          cleanup = () => {
            hls.destroy();
            hlsRef.current = null;
          };
        } else if (fallbackSrc) {
          // HLS.js not supported, use fallback
          video.src = fallbackSrc;
        }
      } catch (error) {
        console.error('Failed to load HLS.js:', error);
        if (fallbackSrc) {
          video.src = fallbackSrc;
        }
      }
    };

    initHls();

    return () => {
      cleanup?.();
    };
  }, [src, fallbackSrc, isHls]);


  // eslint-disable-next-line jsx-a11y/media-has-caption -- Video is decorative/background, captions handled by content
  return (
    <video
      ref={videoRef}
      muted
      poster={poster}
      loop={loop}
      // @ts-expect-error - fetchPriority is valid but not in React types yet
      fetchPriority="high"
      {...props}
    />
  );
};
